/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * captureSyncQueue.js
 *
 * Write-through capture + replay queue for the capture flow. Callers
 * (MediaUploader.jsx for blob uploads, Capture.jsx for item-level
 * mutations) attempt the real network call first; on a network-level
 * failure (offline, dropped connection — not a real server rejection)
 * the operation is persisted via offlineDb and replayed automatically
 * once connectivity returns. Everything stored is plain serializable
 * data (IndexedDB can't hold function values), so replay is always
 * rebuilt from `type + assignmentItemId + payload`, never from a
 * captured closure — this also means queued mutations survive a reload.
 */

import axios from 'axios';
import offlineDb from './offlineDb';
import captureClient, { SESSION_HEADER } from '../api/captureClient';
import { uploadFile as multipartUploadFile } from './multipartUpload';
import { toUtc } from './dateTime';

/**
 * A replay request's cookie may already be gone by the time it fires — the
 * cookie is deliberately capped to expire at the assignment's absolute
 * deadline (see cookieService), which is exactly when a replay is needed.
 * Read the credential persisted in IndexedDB instead (see
 * offlineDb.setSessionCredential) and attach it explicitly so it survives
 * that eviction; captureClient's own interceptor will still overwrite this
 * with the cookie's value when the cookie is still present, which is a no-op
 * since they're kept in sync.
 */
const replayHeaders = async (token) => {
  const credential = await offlineDb.getSessionCredential(token);
  return credential ? { [SESSION_HEADER]: credential } : {};
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const queueListeners = new Map(); // token -> Set<callback>
const flushing = new Set(); // tokens currently flushing
const flushingListeners = new Map(); // token -> Set<callback>

const notifyFlushingChanged = (token) => {
  const listeners = flushingListeners.get(token);
  if (!listeners || listeners.size === 0) return;
  const isFlushing = flushing.has(token);
  listeners.forEach((cb) => cb(isFlushing));
};

/**
 * Subscribe to whether the queue is actively replaying right now — distinct
 * from getPendingCount, which stays > 0 even after a flush pass has already
 * given up on a real (non-network) error and is just waiting for the next
 * reconnect. A UI that blocks interaction while syncing should key off this,
 * not off "something is still queued", or it can trap the user behind an
 * overlay for a stalled item that isn't actually being retried.
 */
export const subscribeFlushing = (token, callback) => {
  if (!flushingListeners.has(token)) flushingListeners.set(token, new Set());
  flushingListeners.get(token).add(callback);
  callback(flushing.has(token));
  return () => {
    flushingListeners.get(token)?.delete(callback);
  };
};

/**
 * Every queued operation (upload or item-level) is represented by exactly
 * one pending `mutations` row, so that table alone is the authoritative
 * "still needs to sync" count — a live in-flight attempt or a permanently
 * failed real-rejection has no mutation row and is correctly excluded.
 */
export const getPendingCount = async (token) => {
  const mutations = await offlineDb.getPendingMutations(token);
  return mutations.length;
};

const notifyQueueChanged = async (token) => {
  const listeners = queueListeners.get(token);
  if (!listeners || listeners.size === 0) return;
  const count = await getPendingCount(token);
  listeners.forEach((cb) => cb(count));
};

/** Subscribe to queue-size changes for a token; returns an unsubscribe fn. */
export const subscribeQueue = (token, callback) => {
  if (!queueListeners.has(token)) queueListeners.set(token, new Set());
  queueListeners.get(token).add(callback);
  getPendingCount(token).then(callback);
  return () => {
    queueListeners.get(token)?.delete(callback);
  };
};

/**
 * The browser's `online` event (and `navigator.onLine`) can flip true
 * before the network path is actually usable — a known WebKit/Safari
 * reconnect race — so a real round-trip to a trivial endpoint confirms
 * reachability before committing to a large multipart upload. Retries
 * with backoff, then gives up so a stuck probe never blocks the queue
 * forever; a failed real upload still falls back to the normal queued
 * retry path either way.
 */
export const waitForRealConnectivity = async (maxAttempts = 4) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      await axios.get(`${API_URL}/health`, { timeout: 5000 });
      return true;
    } catch {
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** attempt));
      }
    }
  }
  return false;
};

export const isNetworkError = (error) => {
  if (!navigator.onLine) return true;
  if (error?.code === 'ERR_NETWORK') return true;
  if (error?.message === 'Network Error') return true;
  // No response object at all usually means the request never reached the server.
  return !error?.response && error?.name !== 'AbortError';
};

const isSessionConflict = (error) => Boolean(error?.response?.status === 409 && error?.response?.data?.sessionConflict);

const isSessionExpired = (error) => {
  const type = error?.response?.data?.type;
  return type === 'session_expired' || type === 'session_inactive' || type === 'no_session' || type === 'no_session_record';
};

// ── Uploads (photo/identifier/video/file steps) ──────────────────────────────

/**
 * Used by MediaUploader.jsx. Always persists the blob locally first, then
 * attempts the real upload if online. On a network-level failure, queues it
 * and resolves a distinguishable `{queued:true, localId, mediaId:null}`
 * marker instead of throwing, so the caller's existing onCapture(file,
 * text, uploadResult) flow can advance without treating it as a hard error.
 *
 * @param {Function} doUpload - performs the real network call and returns
 *   the server response data. Called with the same localId used for offline
 *   storage/replay of this upload (see replayUpload below) so the caller can
 *   send it to the server — a lost-response replay then dedupes server-side
 *   instead of creating a duplicate media file.
 */
export const queuedUpload = async ({
  token,
  assignmentItemId,
  file,
  identifierText = null,
  customerIdentifierText = null,
  duration = null,
  replaceUploadContext = null,
  assignmentItem = null,
  provenanceData = null,
  useMultipart = false,
  offlineSupportEnabled = true,
  capturedByName = null,
  capturedByEmail = null,
  doUpload,
}) => {
  // Offline support off: skip IndexedDB entirely, call the real request
  // directly, and let a failure throw — same as before offline support existed.
  if (!offlineSupportEnabled) {
    return doUpload();
  }

  // Deliberately unwrapped: a local-storage failure here (most commonly
  // QuotaExceededError once the per-token IndexedDB database — asset cache
  // plus every not-yet-synced capture so far — fills the device's quota) is
  // a genuinely different problem from a network failure and must propagate
  // with its native error identity intact, not get swallowed into the
  // network-retry path below. The caller (MediaUploader.jsx's
  // parseUploadError) checks `error.name === 'QuotaExceededError'` before
  // ever looking at connectivity, specifically so this isn't misreported as
  // "no internet" just because the device also happens to be offline.
  const localId = await offlineDb.saveMedia(token, {
    assignmentItemId,
    blob: file,
    fileName: file.name,
    mimeType: file.type,
    identifierText,
    customerIdentifierText,
    duration,
    replaceUploadContext,
    assignmentItem,
    provenanceData,
    useMultipart,
    capturedByName,
    capturedByEmail,
  });

  if (navigator.onLine) {
    try {
      await offlineDb.updateMediaStatus(token, localId, { status: 'syncing' });
      const result = await doUpload(localId);
      await offlineDb.updateMediaStatus(token, localId, { status: 'synced', serverMediaFileId: result?.mediaId ?? null });
      return result;
    } catch (error) {
      if (!isNetworkError(error)) {
        await offlineDb.updateMediaStatus(token, localId, { status: 'failed' });
        throw error;
      }
      // fall through to queue below
    }
  }

  await offlineDb.updateMediaStatus(token, localId, { status: 'pending' });
  const targetKey = offlineDb.buildPhotoTargetKey(assignmentItemId, localId);
  const mutation = await offlineDb.enqueueMutation(token, {
    type: 'upload',
    assignmentItemId,
    targetKey,
    mediaLocalId: localId,
  });
  await offlineDb.supersedeMutations(token, targetKey, mutation.id);
  notifyQueueChanged(token);
  return { queued: true, localId, mediaId: null };
};

export const cancelQueuedUpload = async (token, localId) => {
  if (!localId) return;
  await offlineDb.cancelQueuedUpload(token, localId);
  notifyQueueChanged(token);
};

const buildReplayFile = (media) => new File([media.blob], media.fileName, { type: media.mimeType });

const replayUpload = async (token, media) => {
  const file = buildReplayFile(media);

  if (media.useMultipart) {
    return multipartUploadFile({
      token,
      file,
      assignmentItemId: media.assignmentItemId,
      assignmentItem: media.assignmentItem,
      duration: media.duration,
      provenanceData: media.provenanceData,
      identifierText: media.identifierText,
      customerIdentifierText: media.customerIdentifierText,
      capturedByName: media.capturedByName,
      capturedByEmail: media.capturedByEmail,
      localId: media.localId,
      // Tells the server this came from the offline sync queue, not a live
      // upload — lets it bypass the link-expired block for this request. See
      // replayUpload's FormData branch below and CaptureService.validateToken.
      isOfflineReplay: true,
      // The cookie multipartUploadFile reads by default may already be gone
      // past expiry (see replayHeaders above) — hand it the persisted
      // fallback explicitly.
      sessionTokenOverride: await offlineDb.getSessionCredential(token),
    });
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('assignmentItemId', media.assignmentItemId);
  if (media.assignmentItem) formData.append('assignmentItem', JSON.stringify(media.assignmentItem));
  if (media.provenanceData) formData.append('provenanceData', JSON.stringify(media.provenanceData));
  if (media.identifierText != null) formData.append('identifierText', media.identifierText);
  if (media.customerIdentifierText != null) formData.append('customerIdentifierText', media.customerIdentifierText);
  if (media.capturedByName) formData.append('capturedByName', media.capturedByName);
  if (media.capturedByEmail) formData.append('capturedByEmail', media.capturedByEmail);
  if (media.replaceUploadContext?.replaceExisting) formData.append('replaceExisting', 'true');
  if (media.replaceUploadContext?.replaceMediaFileId) formData.append('replaceMediaFileId', media.replaceUploadContext.replaceMediaFileId);
  if (media.duration) formData.append('duration', media.duration);
  // Lets a lost-response replay dedupe server-side against the live attempt
  // instead of creating a duplicate media file — see queuedUpload/uploadFile.
  formData.append('localId', media.localId);
  // Tells the server this came from the offline sync queue, not a live
  // submission — lets it bypass identifier validation (flagging the item for
  // review) instead of rejecting it and permanently jamming this queue, since
  // there's no in-context UI here for the user to fix the value. Only takes
  // effect server-side when this assignment's offline_support_enabled is
  // actually true — see CaptureService.uploadFile.
  formData.append('isOfflineReplay', 'true');

  const response = await captureClient.post(`/${token}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data', ...(await replayHeaders(token)) },
    timeout: 0,
  });
  return response.data;
};

// ── Item-level mutations (skip / delete item / delete photo / begin-capture) ─

/**
 * Builds the real request for an item-level mutation from plain data only
 * (type + assignmentItemId + payload) — never from a captured closure, so
 * this works identically for a first attempt and a replay after reload.
 */
/**
 * @param isReplay - true only when this request is a replay of a previously
 *   queued mutation (never a live attempt) — see queueItemMutation vs.
 *   processMutation's call sites below. When true, an `isOfflineReplay`
 *   marker is attached so the server can bypass its link-expired block for
 *   this specific request — see capture.service.ts/capture-session.service.ts.
 */
const buildItemMutationRequest = (token, { type, assignmentItemId, payload }, isReplay = false) => {
  const withReplayQuery = (url) => (isReplay ? `${url}${url.includes('?') ? '&' : '?'}isOfflineReplay=true` : url);
  switch (type) {
    case 'deleteItem':
      return async () => captureClient.delete(withReplayQuery(`/${token}/item/${assignmentItemId}`), {
        headers: isReplay ? await replayHeaders(token) : undefined,
      });
    case 'deletePhoto':
      return async () => captureClient.delete(withReplayQuery(`/${token}/item/${assignmentItemId}/media/${payload.mediaFileId}`), {
        headers: isReplay ? await replayHeaders(token) : undefined,
      });
    case 'skip': {
      const formData = new FormData();
      formData.append('assignmentItemId', assignmentItemId);
      formData.append('skip', 'true');
      if (isReplay) formData.append('isOfflineReplay', 'true');
      return async () => captureClient.post(`/${token}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...(isReplay ? await replayHeaders(token) : {}) },
      });
    }
    case 'beginCapture':
      return async () => captureClient.post(`/${token}/begin-capture`, isReplay ? { ...payload, isOfflineReplay: true } : payload, {
        headers: isReplay ? await replayHeaders(token) : undefined,
      });
    case 'complete':
      return async () => captureClient.post(`/${token}/complete`, isReplay ? { isOfflineReplay: true } : undefined, {
        headers: isReplay ? await replayHeaders(token) : undefined,
      });
    case 'saveAndExit':
      return () => captureClient.post(`/${token}/save-and-exit`);
    case 'start':
      return async () => captureClient.post(`/${token}/start`, isReplay ? { isOfflineReplay: true } : undefined, {
        headers: isReplay ? await replayHeaders(token) : undefined,
      });
    case 'provenance':
      return async () => captureClient.post(`/${token}/provenance`, isReplay ? { ...payload, isOfflineReplay: true } : payload, {
        headers: isReplay ? await replayHeaders(token) : undefined,
      });
    default:
      return null;
  }
};

/**
 * Used by Capture.jsx for skip/delete/begin-capture. Attempts immediately if
 * online; on a network-level failure, queues with item-level supersession
 * (a delete/skip cancels any other pending operation queued for that item).
 */
export const queueItemMutation = async ({ token, type, assignmentItemId, payload = {}, offlineSupportEnabled = true }) => {
  const buildRequest = buildItemMutationRequest(token, { type, assignmentItemId, payload });
  if (!buildRequest) throw new Error(`Unknown mutation type: ${type}`);

  // Offline support off: call directly, no queue fallback on failure.
  if (!offlineSupportEnabled) {
    const response = await buildRequest();
    return { queued: false, data: response?.data };
  }

  if (navigator.onLine) {
    try {
      const response = await buildRequest();
      return { queued: false, data: response?.data };
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      // fall through to queue below
    }
  }

  const mutation = await offlineDb.enqueueMutation(token, {
    type,
    assignmentItemId,
    targetKey: assignmentItemId,
    payload,
  });
  if (type === 'deleteItem' || type === 'skip') {
    await offlineDb.supersedeMutationsForItem(token, assignmentItemId, mutation.id);
  }
  notifyQueueChanged(token);
  return { queued: true, data: null };
};

// ── Notes (create / edit / delete) ───────────────────────────────────────────

/**
 * A note's `targetKey` (== its local record's `localId`) is stable for its
 * whole lifetime, so `offlineDb.supersedeMutations` — already generic —
 * doubles as "cancel every other pending op queued for this note".
 */
const generateLocalId = () => window.crypto?.randomUUID?.() ?? `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;

/**
 * Used by NotesModal for create. Writes an optimistic local record first (so
 * it renders immediately either way), then attempts the real POST if online.
 * On a network-level failure it queues an `addNote` mutation carrying the
 * same localId used for the live attempt, so a replay lands idempotently
 * (see capture.service.ts's addNote dedupe-by-localId).
 */
export const queuedAddNote = async ({ token, note, name, email, assignmentItemId = null, offlineSupportEnabled = true }) => {
  if (!offlineSupportEnabled) {
    const response = await captureClient.post(`/${token}/notes`, { note, name, email, ...(assignmentItemId ? { assignmentItemId } : {}) });
    return { note: response.data, queued: false };
  }

  const localId = generateLocalId();
  const localRecord = {
    localId,
    id: localId,
    serverId: null,
    assignmentItemId,
    note,
    name,
    email,
    timestamp: toUtc(new Date()),
    updatedAt: null,
    deletedAt: null,
    canManage: true,
    syncStatus: 'pending',
    createdAt: Date.now(),
  };
  await offlineDb.upsertNoteLocal(token, localRecord);

  const doAdd = () => captureClient.post(`/${token}/notes`, { note, name, email, assignmentItemId, localId });

  if (navigator.onLine) {
    try {
      const response = await doAdd();
      const synced = { ...localRecord, ...response.data, localId, serverId: response.data.id, syncStatus: 'synced' };
      await offlineDb.upsertNoteLocal(token, synced);
      return { note: synced, queued: false };
    } catch (error) {
      if (!isNetworkError(error)) {
        await offlineDb.removeNoteLocal(token, localId);
        throw error;
      }
      // fall through to queue below
    }
  }

  await offlineDb.enqueueMutation(token, {
    type: 'addNote',
    assignmentItemId,
    targetKey: localId,
    payload: { note, name, email, assignmentItemId, localId },
  });
  notifyQueueChanged(token);
  return { note: localRecord, queued: true };
};

/**
 * Used by NotesModal for edit. `localNote` is the current local record. If
 * it's still an unsynced local-only create (no serverId yet), the edit is
 * folded directly into that pending addNote's payload instead of being
 * queued separately — there's no server note to PATCH yet.
 */
export const queuedUpdateNote = async ({ token, localNote, note, name, email, offlineSupportEnabled = true }) => {
  if (!offlineSupportEnabled) {
    // A plain (non-offline) fetch never round-trips through offlineDb, so
    // these notes only ever carry the server's own `id`, never `serverId`.
    const response = await captureClient.patch(`/${token}/notes/${localNote.serverId ?? localNote.id}`, { note, name, email });
    return { note: response.data, queued: false };
  }

  if (!localNote.serverId) {
    const pending = (await offlineDb.getPendingMutations(token)).find(
      (m) => m.type === 'addNote' && m.targetKey === localNote.localId
    );
    if (pending) {
      await offlineDb.updateMutation(token, pending.id, { payload: { ...pending.payload, note } });
    }
    const updated = { ...localNote, note, updatedAt: toUtc(new Date()) };
    await offlineDb.upsertNoteLocal(token, updated);
    notifyQueueChanged(token);
    return { note: updated, queued: true };
  }

  // Cancel any older pending op still queued for this note (e.g. an earlier
  // offline edit that hasn't replayed yet) before doing anything new — the
  // latest write always wins, whether it lands immediately or gets queued.
  await offlineDb.supersedeMutations(token, localNote.localId, null);

  const doUpdate = () => captureClient.patch(`/${token}/notes/${localNote.serverId}`, { note, name, email });

  if (navigator.onLine) {
    try {
      const response = await doUpdate();
      const synced = { ...localNote, ...response.data, localId: localNote.localId, serverId: localNote.serverId, syncStatus: 'synced' };
      await offlineDb.upsertNoteLocal(token, synced);
      return { note: synced, queued: false };
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      // fall through to queue below
    }
  }

  await offlineDb.enqueueMutation(token, {
    type: 'updateNote',
    assignmentItemId: localNote.assignmentItemId,
    targetKey: localNote.localId,
    payload: { note, name, email },
  });
  const updated = { ...localNote, note, updatedAt: toUtc(new Date()), syncStatus: 'pending' };
  await offlineDb.upsertNoteLocal(token, updated);
  notifyQueueChanged(token);
  return { note: updated, queued: true };
};

/**
 * Used by NotesModal for delete. A note that never synced is cancelled
 * outright (its pending addNote mutation is superseded and the local record
 * removed) — there's nothing server-side to tell about the delete.
 */
export const queuedDeleteNote = async ({ token, localNote, name, email, offlineSupportEnabled = true }) => {
  if (!offlineSupportEnabled) {
    await captureClient.delete(`/${token}/notes/${localNote.serverId ?? localNote.id}`, { data: { name, email } });
    return { queued: false };
  }

  if (!localNote.serverId) {
    await offlineDb.supersedeMutations(token, localNote.localId, null);
    await offlineDb.removeNoteLocal(token, localNote.localId);
    notifyQueueChanged(token);
    return { queued: true };
  }

  // Cancel any older pending op still queued for this note (e.g. an earlier
  // offline edit that hasn't replayed yet) — a delete always wins over it,
  // whether the delete itself lands immediately or gets queued.
  await offlineDb.supersedeMutations(token, localNote.localId, null);

  const doDelete = () => captureClient.delete(`/${token}/notes/${localNote.serverId}`, { data: { name, email } });

  if (navigator.onLine) {
    try {
      await doDelete();
      await offlineDb.removeNoteLocal(token, localNote.localId);
      notifyQueueChanged(token);
      return { queued: false };
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      // fall through to queue below
    }
  }

  await offlineDb.enqueueMutation(token, {
    type: 'deleteNote',
    assignmentItemId: localNote.assignmentItemId,
    targetKey: localNote.localId,
    payload: { name, email },
  });
  await offlineDb.upsertNoteLocal(token, { ...localNote, deletedAt: toUtc(new Date()), syncStatus: 'pending' });
  notifyQueueChanged(token);
  return { queued: true };
};

/**
 * Used by NotesModal to populate its list. Reconciles the local mirror with
 * a fresh GET when online, then always reads back from the local mirror — so
 * a network-level failure or being offline falls through to the last-known
 * list plus anything queued locally, instead of the previous behavior of
 * wiping the list on any fetch error. A real server rejection (non-network)
 * still propagates to the caller instead of being silently swallowed here.
 */
export const fetchNotes = async ({ token, name, email, offlineSupportEnabled = true }) => {
  if (!offlineSupportEnabled) {
    const response = await captureClient.get(`/${token}/notes`, { params: { name, email } });
    return response.data;
  }

  if (navigator.onLine) {
    try {
      const response = await captureClient.get(`/${token}/notes`, { params: { name, email } });
      await offlineDb.reconcileNotesFromServer(token, response.data);
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      // Network/transient failure — fall through to the local mirror below.
    }
  }

  return offlineDb.getNotesLocal(token);
};

const NOTE_MUTATION_TYPES = ['addNote', 'updateNote', 'deleteNote'];

/**
 * Replays a queued note create/edit/delete. updateNote/deleteNote are only
 * ever queued for a note that had already synced (see queuedUpdateNote/
 * queuedDeleteNote — an edit/delete of a still-unsynced create is folded
 * into that pending addNote instead), so their serverId is always already
 * known from the local record — no cross-mutation ordering dependency.
 */
const processNoteMutation = async (token, mutation, context) => {
  try {
    const headers = await replayHeaders(token);
    if (mutation.type === 'addNote') {
      const response = await captureClient.post(`/${token}/notes`, { ...mutation.payload, isOfflineReplay: true }, { headers });
      const localNote = await offlineDb.getNoteLocal(token, mutation.targetKey);
      if (localNote) {
        await offlineDb.upsertNoteLocal(token, { ...localNote, ...response.data, localId: mutation.targetKey, serverId: response.data.id, syncStatus: 'synced' });
      }
      await offlineDb.updateMutation(token, mutation.id, { status: 'synced' });
      context.onMutationSynced?.({ type: 'addNote', localId: mutation.targetKey, result: response.data });
      return 'synced';
    }

    const localNote = await offlineDb.getNoteLocal(token, mutation.targetKey);
    if (!localNote?.serverId) {
      // Nothing sane to replay against — shouldn't happen given how these
      // are enqueued, but don't hot-loop on a mutation that can never apply.
      await offlineDb.updateMutation(token, mutation.id, { status: 'cancelled' });
      return 'skip-loop';
    }

    if (mutation.type === 'updateNote') {
      const response = await captureClient.patch(`/${token}/notes/${localNote.serverId}`, { ...mutation.payload, isOfflineReplay: true }, { headers });
      await offlineDb.upsertNoteLocal(token, { ...localNote, ...response.data, localId: mutation.targetKey, serverId: localNote.serverId, syncStatus: 'synced' });
      await offlineDb.updateMutation(token, mutation.id, { status: 'synced' });
      context.onMutationSynced?.({ type: 'updateNote', localId: mutation.targetKey, result: response.data });
      return 'synced';
    }

    // deleteNote
    await captureClient.delete(`/${token}/notes/${localNote.serverId}`, { data: { ...mutation.payload, isOfflineReplay: true }, headers });
    await offlineDb.removeNoteLocal(token, mutation.targetKey);
    await offlineDb.updateMutation(token, mutation.id, { status: 'synced' });
    context.onMutationSynced?.({ type: 'deleteNote', localId: mutation.targetKey });
    return 'synced';
  } catch (error) {
    return handleReplayError(token, mutation, error, context);
  }
};

// ── Flush / replay engine ─────────────────────────────────────────────────────

/** Returns 'synced' | 'skip-loop' | 'session-conflict' | 'session-expired' | 'error'. */
const processMutation = async (token, mutation, context) => {
  if (NOTE_MUTATION_TYPES.includes(mutation.type)) {
    return processNoteMutation(token, mutation, context);
  }

  if (mutation.type === 'upload') {
    const media = await offlineDb.getMedia(token, mutation.mediaLocalId);
    if (!media || media.status === 'cancelled') {
      await offlineDb.updateMutation(token, mutation.id, { status: 'cancelled' });
      return 'skip-loop';
    }
    try {
      await offlineDb.updateMediaStatus(token, mutation.mediaLocalId, { status: 'syncing' });
      const result = await replayUpload(token, media);
      // Re-check for a cancel that raced the in-flight request.
      const latest = await offlineDb.getMedia(token, mutation.mediaLocalId);
      if (latest?.status === 'cancelled') {
        context.onOrphanedSync?.({ assignmentItemId: mutation.assignmentItemId, mediaFileId: result?.mediaId });
        await offlineDb.updateMutation(token, mutation.id, { status: 'cancelled' });
        return 'skip-loop';
      }
      await offlineDb.updateMediaStatus(token, mutation.mediaLocalId, { status: 'synced', serverMediaFileId: result?.mediaId ?? null });
      await offlineDb.updateMutation(token, mutation.id, { status: 'synced' });
      context.onMutationSynced?.({ type: 'upload', assignmentItemId: mutation.assignmentItemId, mediaLocalId: mutation.mediaLocalId, result });
      return 'synced';
    } catch (error) {
      return handleReplayError(token, mutation, error, context);
    }
  }

  const buildRequest = buildItemMutationRequest(token, mutation, true);
  if (!buildRequest) {
    await offlineDb.updateMutation(token, mutation.id, { status: 'failed', lastError: 'unknown mutation type' });
    return 'skip-loop';
  }
  try {
    const response = await buildRequest();
    await offlineDb.updateMutation(token, mutation.id, { status: 'synced' });
    context.onMutationSynced?.({ type: mutation.type, assignmentItemId: mutation.assignmentItemId, payload: mutation.payload, result: response?.data });
    return 'synced';
  } catch (error) {
    return handleReplayError(token, mutation, error, context);
  }
};

const handleReplayError = async (token, mutation, error, context = {}) => {
  if (isSessionConflict(error)) return 'session-conflict';
  if (isSessionExpired(error)) return 'session-expired';
  await offlineDb.updateMutation(token, mutation.id, {
    status: 'pending',
    retryCount: (mutation.retryCount || 0) + 1,
    lastError: error?.message || 'Unknown error',
  });
  // A plain network blip (offline, dropped connection) is expected and
  // silently retried — the connectivity banner already covers that. Only a
  // real server-side rejection (validation failure, 404, 500, ...) is
  // actually worth interrupting the user about.
  if (!isNetworkError(error)) {
    context.onSyncError?.({ type: mutation.type, error });
  }
  return 'error';
};

/**
 * Flush the queue: replays pending mutations in creation order until the
 * queue is empty or a stop condition is hit (session conflict/expiry,
 * connectivity drop, or a transient error we don't want to hot-loop on).
 *
 * context: {
 *   reconcile: async () => completedItemsMap,   // re-fetches assignment state (fetchCaptureData)
 *   onMutationSynced: (info) => void,
 *   onOrphanedSync: (info) => void,
 *   onSessionExpiredUnrecoverable: (error) => void,
 *   onSyncError: ({ type, error }) => void,   // a real (non-network) rejection during replay
 * }
 */
export const flushQueue = async (token, context = {}) => {
  if (flushing.has(token)) return;
  flushing.add(token);
  notifyFlushingChanged(token);
  // Bounds the reconcile-then-retry cycle below to one attempt per mutation —
  // without this, a mutation whose session-expired error reconcile can't
  // actually fix (a genuinely dead session, not just a stale one) retries
  // forever with no delay, hot-looping both the reconcile call and the
  // mutation's own request.
  let sessionExpiredMutationId = null;
  let sessionExpiredRetries = 0;
  try {
    for (;;) {
      if (!navigator.onLine) break;
      const mutations = await offlineDb.getPendingMutations(token);
      if (mutations.length === 0) break;
      const mutation = mutations[0];

      const outcome = await processMutation(token, mutation, context);
      notifyQueueChanged(token);

      if (outcome === 'synced' || outcome === 'skip-loop') {
        sessionExpiredMutationId = null;
        sessionExpiredRetries = 0;
        continue;
      }
      if (outcome === 'session-conflict') break; // existing capture-session-conflict UI already handles this

      if (outcome === 'session-expired') {
        sessionExpiredRetries = mutation.id === sessionExpiredMutationId ? sessionExpiredRetries + 1 : 1;
        sessionExpiredMutationId = mutation.id;

        if (sessionExpiredRetries > 1) {
          // Already reconciled once for this exact mutation and it's still
          // session-expired — the session is genuinely dead, not just stale.
          context.onSessionExpiredUnrecoverable?.(new Error('Session could not be refreshed after being offline for a while.'));
          break;
        }

        try {
          const completedItems = await context.reconcile?.();
          if (completedItems?.[mutation.assignmentItemId]) {
            // Landed before the session actually died — drop it and keep going.
            await offlineDb.updateMutation(token, mutation.id, { status: 'synced' });
            if (mutation.type === 'upload') {
              await offlineDb.updateMediaStatus(token, mutation.mediaLocalId, { status: 'synced' });
            }
            sessionExpiredMutationId = null;
            sessionExpiredRetries = 0;
          }
          // Otherwise still genuinely pending: leave it queued and retry once
          // against whatever session the reconciliation just (re)established.
          continue;
        } catch (reconcileError) {
          context.onSessionExpiredUnrecoverable?.(reconcileError);
          break;
        }
      }

      // 'error' — transient failure, stop this pass; the next `online` event
      // or manual retry will pick the queue back up.
      break;
    }
  } finally {
    flushing.delete(token);
    notifyFlushingChanged(token);
    notifyQueueChanged(token);
  }
};

export const isQueueFlushing = (token) => flushing.has(token);

export default {
  subscribeQueue,
  subscribeFlushing,
  getPendingCount,
  queuedUpload,
  queueItemMutation,
  cancelQueuedUpload,
  queuedAddNote,
  queuedUpdateNote,
  queuedDeleteNote,
  fetchNotes,
  flushQueue,
  isQueueFlushing,
  isNetworkError,
  waitForRealConnectivity,
};
